
    // IMPORTS
    import { computed, defineComponent, ref, watchEffect, watch } from 'vue'
    import { useRoute, RouterView } from 'vue-router'
    import router from '@/router'
    import { buildLocalization } from '@/util'
    import { useEquipmentStore } from '@/store/equipment'
    import { useUserStore } from '@/store/user'

    import PortalPage from '@/components/common/portal-page.vue'

    import { EquipmentMasterData, IEquipmentMasterDataDTO } from '@/types/equipment'
    import { INavigationItem } from '@/types/INavigationItem'

    export default defineComponent({
        name: 'equipmentPage',

        components: {
            PortalPage,
            RouterView
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const route = useRoute()
            const serialNo = route.params.sn as string

            if (!serialNo) {
                router.push("/")
            }

            const userStore = useUserStore()
            const equipmentStore = useEquipmentStore()
            const { localize } = buildLocalization()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const equipment = ref<IEquipmentMasterDataDTO>({
                equipmentNo: 0,
                description: localize('Loading'),
                materialNo: 0,
                serialNo: "",
            } as IEquipmentMasterDataDTO)

            const publicData = { serialNo, equipment }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const loginType = computed(() => userStore.loginType)
            const isDeviceLogin = computed(() => loginType.value == 'device')
            const equipmentData = computed(() => equipmentStore.masterData == null ? null : new EquipmentMasterData(equipmentStore.masterData))
            const hasLoaded = computed(() => equipmentData.value != null)
            const serialNoInfo = computed(() => equipmentData.value?.serialNo ? `${equipmentData.value.serialNo} ` : '')
            const pageTitle = computed(() => `${serialNoInfo.value}${equipmentData.value?.description}`)

            const navigationItems = computed(() => [
                {
                    textName: 'Changes',
                    icon: { name: 'warning', color: 'yellow' },
                    routeName: 'equipment-deviations',
                    condition: () => userStore.isAdmin && (equipmentStore.bomDeviations?.length ?? 0) > 0,
                    replace: true
                },
                {
                    textName: 'SpareParts',
                    routeName: 'equipment-spareparts',
                    condition: () => !isDeviceLogin.value, // added sep 2025
                    replace: true
                },
                {
                    textName: 'Configuration',
                    routeName: 'equipment-conf',
                    condition: () => !isDeviceLogin.value,
                    replace: true
                },
                {
                    textName: 'Documents',
                    routeName: 'equipment-documents',
                    condition: () => equipmentStore.showDocumentTab === true,
                    replace: true
                },
                {
                    textName: 'MasterData',
                    routeName: 'equipment-data',
                    icon: equipmentData.value?.adminOnly ? { name: 'lock', color: 'red' } : null,
                    condition: () => !isDeviceLogin.value,
                    replace: true
                },
                {
                    textName: 'Software',
                    routeName: 'equipment-software',
                    icon: equipmentData.value?.isMajorUpgrade ? { name: 'circle', color: 'red' } : null,
                    condition: () => equipmentData.value?.software != null,
                    replace: true
                }
            ] as INavigationItem[])

            const computedData = { isDeviceLogin, pageTitle, navigationItems }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const publicMethods = { localize }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function setDevice(currency: string | undefined) {
                if (currency != null) {
                    equipmentStore.setDevice(serialNo).catch(err => console.log(err))
                }
            }

            function performRedirectIfNeeded() {
                const allowed = navigationItems.value.filter(n => !n.condition || n.condition())
                if (allowed.length === 0) return
                const currentRouteName = route.name as string | undefined
                const isParentRoute = currentRouteName === 'equipment'
                const isCurrentAllowed = allowed.some(n => n.routeName === currentRouteName)
                if (isParentRoute || !isCurrentAllowed) {
                    router.replace({ name: allowed[0].routeName!, params: route.params })
                }
            }

            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            watchEffect(() => setDevice(userStore.currency))

            // Perform initial redirect only AFTER data is loaded (avoids premature redirect before Software/MasterData become available)
            let initialRedirectDone = false
            watch(() => hasLoaded.value, loaded => {
                if (!loaded || initialRedirectDone) return
                initialRedirectDone = true
                performRedirectIfNeeded()
            })

            // Also react if navigationItems availability changes later (e.g. permissions change)
            watch(() => navigationItems.value.map(n => ({ r: n.routeName, a: !n.condition || n.condition() })), () => {
                if (!hasLoaded.value) return
                performRedirectIfNeeded()
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
